import {
  ParsedTestCard,
  ProsperaOrganType,
} from "@app/provider/testData/types";
import { TestTitle } from "@app/components/common";
import { Link } from "@natera/navigation";
import { routes } from "@app/routing";
import { getTestCardLinkAriaLabel } from "@app/utils";
import Svg from "@natera/material/lib/svg";
import arrowRightIcon from "@assets/svg/icons/chevron-centered.svg";
import React, { FC, useMemo } from "react";
import { useIntl } from "react-intl";
import heartIcon from "@assets/svg/icons/organs/heart.svg";
import kidneysIcon from "@assets/svg/icons/organs/kidneys.svg";
import lungsIcon from "@assets/svg/icons/organs/lungs.svg";

interface Props {
  testCard: ParsedTestCard;
  leftSideClickable?: boolean;
  isHistory?: boolean;
}

const TestCardTitle: FC<Props> = ({
  testCard,
  leftSideClickable,
  isHistory,
}) => {
  const intl = useIntl();

  // Use i18nKeys.title if available (rules-based cards), otherwise fall back to legacy mapping
  const title = useMemo(() => {
    return (
      <TestTitle
        testType={testCard.testType}
        i18nKey={testCard.i18nKeys?.title}
      />
    );
  }, [testCard.testType, testCard.i18nKeys?.title, intl]);

  // Organ icons are decorative — the adjacent <h2> already spells out the
  // organ in the visible title (e.g. "Prospera for kidney"). aria-hidden
  // keeps screen readers from re-announcing the same information as an
  // unlabeled graphic.
  const renderOrganSvg = (organ: string) => {
    switch (organ) {
      case ProsperaOrganType.HEART:
        return <Svg content={heartIcon} aria-hidden="true" />;
      case ProsperaOrganType.KIDNEY:
      case ProsperaOrganType.SPK:
        return <Svg content={kidneysIcon} aria-hidden="true" />;
      case ProsperaOrganType.LUNG:
        return <Svg content={lungsIcon} aria-hidden="true" />;
      default:
        return;
    }
  };

  if (leftSideClickable) {
    return (
      <Link
        to={routes.testDetailsPage(testCard.orderUidPublic, testCard.uid)}
        state={isHistory ? { from: routes.testHistory } : undefined}
        className="upp-test-card-title"
        aria-label={getTestCardLinkAriaLabel(
          testCard.testType,
          testCard.organType,
          intl
        )}
      >
        {testCard.organType && renderOrganSvg(testCard.organType)}
        <h2>{title}</h2>
        <Svg
          className="upp-test-card-title__arrow-svg"
          content={arrowRightIcon}
        />
      </Link>
    );
  }

  return (
    <div className="upp-test-card-title">
      {testCard.organType && renderOrganSvg(testCard.organType)}
      <h2>{title}</h2>
    </div>
  );
};

export default TestCardTitle;
